/**
 * Single owner of permission resolution for the session.
 *
 * The set resolved here serves both React UI (via `useAuthZ`) and route
 * filtering (via `useAuthZPermissions`), so org settings cannot reach one and
 * not the other. See docs/authz.md Usage → Gate React UI (useAuthZ).
 */
import { type ReactNode, useMemo, useRef } from "react";

import { AuthZContext } from "./AuthZContext";
import type { AuthZSession } from "./authZSession";
import { authZFromPrincipal, emptyPermissionSet } from "./can";
import { compileOrgPolicy, orgPolicyKey } from "./orgPolicy";

export function AuthZProvider({
  session,
  children,
}: {
  session: AuthZSession;
  children: ReactNode;
}) {
  // structuralSharing is disabled on current-user, so every refetch yields a
  // new organization object for an identical payload. Fingerprint content into
  // sessionKey; keep the latest session in a ref so useMemo does not close
  // over a render-local binding with incomplete deps.
  const sessionKey =
    session === null
      ? null
      : `${session.role}:${orgPolicyKey(compileOrgPolicy(session.organization))}`;

  const sessionRef = useRef(session);
  sessionRef.current = session;

  const value = useMemo(() => {
    // sessionKey is the content fingerprint; reading it ties the memo to policy
    // changes without depending on organization identity.
    const current = sessionKey === null ? null : sessionRef.current;
    if (current === null) {
      const permissions = emptyPermissionSet();
      return { permissions, can: () => false };
    }

    return authZFromPrincipal({ role: current.role }, current.organization);
  }, [sessionKey]);

  return (
    <AuthZContext.Provider value={value}>{children}</AuthZContext.Provider>
  );
}
